import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Clock, Copy, FileText, ImageIcon, Loader2, RefreshCw, Send, Share2, Sparkles, Trash2, XCircle } from "lucide-react";
import { useTheme } from "next-themes";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { AdminSocialSubNav } from "@/components/admin/AdminSocialSubNav";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAdminSession } from "@/hooks/use-admin-session";

type PostStatus = "draft" | "scheduled" | "published" | "failed";

interface Post {
  id: string;
  caption: string;
  image_url: string | null;
  status: PostStatus;
  scheduled_at: string | null;
  published_at: string | null;
  error_message: string | null;
  created_at: string;
  comment_reply: string | null;
  dm_content: string | null;
}

const STATUS_BADGE: Record<PostStatus, { label: string; className: string }> = {
  draft:     { label: "Taslak",     className: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300" },
  scheduled: { label: "Zamanlandı", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  published: { label: "Yayınlandı", className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  failed:    { label: "Hata",       className: "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300" },
};

const API = "/api/social/instagram";

export default function AdminSocialContentGenerator() {
  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const authenticated = !!sessionQuery.data?.authenticated;
  const configured = !!sessionQuery.data?.configured;
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";
  const { toast } = useToast();

  // Form state
  const [postType, setPostType] = useState<string>("gunun_enerjisi");
  const [caption, setCaption] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imagePrompt, setImagePrompt] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState<"draft" | "scheduled" | "publish" | null>(null);

  // Image generation state
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // List state
  const [posts, setPosts] = useState<Post[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<string | null>(null);
  const [editCommentReply, setEditCommentReply] = useState('');
  const [editDmContent, setEditDmContent] = useState('');

  const fetchPosts = useCallback(async () => {
    try {
      const r = await fetch(`${API}/posts`);
      const json = await r.json();
      if (json.ok) setPosts(json.posts ?? []);
    } catch {
      // sessizce geç
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authenticated) fetchPosts();
  }, [authenticated, fetchPosts]);

  async function handleLunaGenerate() {
    setGenerating(true);
    try {
      const r = await fetch(`${API}/luna-generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic: caption.trim() || "günün enerjisi", postType }),
      });
      const json = await r.json();
      if (json.ok) {
        const text = json.text || '';

        // IMAGE_PROMPT'u caption'dan ayır
        const imagePromptMatch = text.match(/IMAGE_PROMPT:\s*([\s\S]+?)$/i);
        if (imagePromptMatch) {
          setImagePrompt(imagePromptMatch[1].trim());
        } else {
          setImagePrompt(json.imagePrompt ?? "");
        }

        // Caption'dan IMAGE_PROMPT'u temizle
        const cleanCaption = text.replace(/\n?IMAGE_PROMPT:[\s\S]*$/i, '').trim();
        setCaption(cleanCaption);

        setGeneratedImage(null);
        setSelectedImage(null);
        toast({ title: "Luna yazdı ✨" });
      } else {
        toast({ title: "Hata", description: json.error, variant: "destructive" });
      }
    } catch {
      toast({ title: "Bağlantı hatası", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  }

  async function handleGenerateImage() {
    if (!imagePrompt) return;
    setGeneratingImage(true);
    try {
      const r = await fetch(`${API}/generate-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: imagePrompt }),
      });
      const json = await r.json();
      if (json.ok) {
        setGeneratedImage(json.image ?? json.imageUrl);
        toast({ title: "Görsel hazır ✨" });
      } else {
        toast({ title: "Görsel hatası", description: json.error, variant: "destructive" });
      }
    } finally {
      setGeneratingImage(false);
    }
  }

  function handleRegenerateImage() {
    handleGenerateImage();
  }

  async function handleUseImage() {
    if (!generatedImage) return;

    if (!generatedImage.startsWith("data:")) {
      setSelectedImage(generatedImage);
      toast({ title: "Görsel seçildi ✅" });
      return;
    }

    try {
      toast({ title: "Görsel yükleniyor..." });
      const r = await fetch(`${API}/upload-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageBase64: generatedImage }),
      });
      const json = await r.json();
      if (json.ok) {
        setSelectedImage(json.imageUrl);
        toast({ title: "Görsel hazır ✅" });
      } else {
        toast({ title: "Yükleme hatası", description: json.error, variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Hata", description: e.message, variant: "destructive" });
    }
  }

  async function createPost(status: "draft" | "scheduled"): Promise<Post | null> {
    const imageToUse = selectedImage?.startsWith("data:") ? null : selectedImage;

    if (selectedImage?.startsWith("data:") && !imageUrl.trim()) {
      toast({
        title: "Görsel önce yüklenmeli",
        description: "Görsel URL alanına public bir URL yapıştırın",
        variant: "destructive",
      });
      return null;
    }

    const body: Record<string, any> = {
      caption,
      image_url: imageToUse || imageUrl.trim() || null,
      status,
    };
    if (status === "scheduled" && scheduledAt) body.scheduled_at = scheduledAt;

    const r = await fetch(`${API}/posts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const json = await r.json();
    return json.ok ? json.post : null;
  }

  async function handleDraft() {
    if (!caption.trim()) return;
    setSaving("draft");
    try {
      const post = await createPost("draft");
      if (post) {
        toast({ title: "Taslak kaydedildi" });
        setCaption(""); setImageUrl(""); setScheduledAt(""); setImagePrompt(""); setGeneratedImage(null); setSelectedImage(null);
        await fetchPosts();
      } else {
        toast({ title: "Hata", variant: "destructive" });
      }
    } finally {
      setSaving(null);
    }
  }

  async function handleSchedule() {
    if (!caption.trim() || !scheduledAt) return;
    setSaving("scheduled");
    try {
      const post = await createPost("scheduled");
      if (post) {
        toast({ title: "Zamanlandı" });
        setCaption(""); setImageUrl(""); setScheduledAt(""); setImagePrompt(""); setGeneratedImage(null); setSelectedImage(null);
        await fetchPosts();
      } else {
        toast({ title: "Hata", variant: "destructive" });
      }
    } finally {
      setSaving(null);
    }
  }

  async function handlePublishNow() {
    if (!caption.trim()) return;
    setSaving("publish");
    try {
      const post = await createPost("draft");
      if (!post) {
        toast({ title: "Gönderi oluşturulamadı", variant: "destructive" });
        return;
      }
      const r = await fetch(`${API}/posts/${post.id}/publish`, { method: "POST" });
      const json = await r.json();
      if (json.ok) {
        toast({ title: "Yayınlandı ✅" });
        setCaption(""); setImageUrl(""); setScheduledAt(""); setImagePrompt(""); setGeneratedImage(null); setSelectedImage(null);
      } else {
        toast({ title: "Yayın hatası", description: json.error, variant: "destructive" });
      }
      await fetchPosts();
    } finally {
      setSaving(null);
    }
  }

  async function handlePublishPost(id: string) {
    setActionId(id);
    try {
      const r = await fetch(`${API}/posts/${id}/publish`, { method: "POST" });
      const json = await r.json();
      if (json.ok) {
        toast({ title: "Yayınlandı ✅" });
      } else {
        toast({ title: "Hata", description: json.error, variant: "destructive" });
      }
      await fetchPosts();
    } finally {
      setActionId(null);
    }
  }

  async function handleDelete(id: string) {
    setActionId(id);
    try {
      await fetch(`${API}/posts/${id}`, { method: "DELETE" });
      await fetchPosts();
      toast({ title: "Silindi" });
    } finally {
      setActionId(null);
    }
  }

  function formatDate(iso: string | null) {
    if (!iso) return null;
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  }

  if (sessionQuery.isLoading) {
    return <div className="p-4 text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
                mode="links"
                isDark={isDark}
                onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
              />
              <div className="flex items-center gap-2">
                <Share2 className="h-5 w-5 text-slate-500 dark:text-slate-300" />
                <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">
                  Sosyal Medya
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              className="rounded-full"
              size="sm"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
            >
              Çıkış
            </Button>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <AdminSocialSubNav />

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
            <div className="mb-6">
              <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">İçerik Üretici</div>
              <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Instagram gönderisi oluştur, zamanla veya hemen yayınla
              </div>
            </div>

            <div className="grid gap-8 lg:grid-cols-2">
              {/* SOL PANEL — Gönderi Oluştur */}
              <div className="space-y-4">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Gönderi Oluştur
                </div>

                {/* İçerik Türü */}
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600 dark:text-slate-400">
                    İçerik Türü
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: "gunun_enerjisi", label: "☀️ Günün Enerjisi" },
                      { value: "numeroloji_verisi", label: "🔢 Numeroloji Verisi" },
                      { value: "kullanici_geri_bildirimi", label: "💬 Geri Bildirim" },
                      { value: "genel", label: "✨ Genel" },
                    ].map((t) => (
                      <button
                        key={t.value}
                        onClick={() => setPostType(t.value)}
                        className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                          postType === t.value
                            ? "border-purple-400 bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                            : "border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Caption */}
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600 dark:text-slate-400">
                    Caption
                  </label>
                  <p className="mb-1 text-xs text-slate-500">
                    {postType === "gunun_enerjisi"
                      ? "Boş bırakın — tarih otomatik hesaplanır."
                      : postType === "kullanici_geri_bildirimi"
                      ? "Kullanıcı geri bildirimini buraya yaz, Luna dönüştürsün."
                      : "Bir konu yaz, Luna içeriği üretsin."}
                  </p>
                  <Textarea
                    rows={6}
                    placeholder={
                      postType === "gunun_enerjisi"
                        ? "Boş bırakın — tarih otomatik hesaplanır"
                        : postType === "kullanici_geri_bildirimi"
                        ? "Kullanıcı geri bildirimini buraya yaz..."
                        : "Konu yaz (örn: 7 yaşam yolu sayısı, karmik borç...)"
                    }
                    className="border-slate-200 bg-white text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2 w-full border-purple-200 text-purple-700 hover:bg-purple-50 dark:border-purple-700/50 dark:text-purple-300 dark:hover:bg-purple-950/30"
                    onClick={handleLunaGenerate}
                    disabled={generating}
                  >
                    {generating ? (
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="mr-2 h-3.5 w-3.5" />
                    )}
                    {generating ? "Luna yazıyor…" : "Luna AI ile Yaz"}
                  </Button>
                </div>

                {/* Görsel Prompt (Luna'dan gelen) */}
                {imagePrompt && (
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400">
                      Görsel Prompt
                    </label>
                    <div className="flex gap-2">
                      <input
                        readOnly
                        className="h-10 flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                        value={imagePrompt}
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(imagePrompt);
                          toast({ title: "Kopyalandı" });
                        }}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
                        title="Kopyala"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {/* Görsel Oluştur butonu */}
                    <Button
                      onClick={handleGenerateImage}
                      disabled={!imagePrompt || generatingImage}
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                    >
                      {generatingImage ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <ImageIcon className="mr-2 h-4 w-4" />
                      )}
                      {generatingImage ? "Görsel oluşturuluyor…" : "Görsel Oluştur"}
                    </Button>

                    {/* Görsel önizleme */}
                    {generatedImage && (
                      <>
                        <div className="relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
                          <img
                            src={generatedImage}
                            alt="Üretilen görsel"
                            className="w-full object-cover"
                            style={{ minHeight: "200px" }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "";
                              toast({ title: "Görsel yüklenemedi", variant: "destructive" });
                            }}
                          />
                          {selectedImage === generatedImage && (
                            <div className="absolute left-2 top-2 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-medium text-white">
                              Seçildi
                            </div>
                          )}
                        </div>
                        <div className="mt-2 flex gap-2">
                          <button
                            onClick={handleRegenerateImage}
                            disabled={generatingImage}
                            className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300"
                          >
                            <RefreshCw className="h-3 w-3" />
                            Tekrar Üret
                          </button>
                          <button
                            onClick={handleUseImage}
                            className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700"
                          >
                            ✓ Kullan
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Görsel URL */}
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600 dark:text-slate-400">
                    Görsel URL (opsiyonel)
                  </label>
                  <input
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                  />
                </div>

                {/* Zamanlama */}
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600 dark:text-slate-400">
                    Yayın Zamanı (opsiyonel)
                  </label>
                  <input
                    type="datetime-local"
                    className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                  />
                </div>

                {/* Aksiyon butonları */}
                <div className="flex gap-2 pt-1">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={handleDraft}
                    disabled={!caption.trim() || saving !== null}
                  >
                    {saving === "draft" ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <FileText className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Taslak
                  </Button>
                  <Button
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    onClick={handleSchedule}
                    disabled={!caption.trim() || !scheduledAt || saving !== null}
                  >
                    {saving === "scheduled" ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Clock className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Zamanla
                  </Button>
                  <Button
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    onClick={handlePublishNow}
                    disabled={!caption.trim() || saving !== null}
                  >
                    {saving === "publish" ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Şimdi Yayınla
                  </Button>
                </div>
              </div>

              {/* SAĞ PANEL — Gönderiler Listesi */}
              <div>
                <div className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Gönderiler
                </div>

                {listLoading ? (
                  <div className="flex h-40 items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                  </div>
                ) : posts.length === 0 ? (
                  <div className="flex h-40 flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 text-slate-400 dark:border-slate-700 dark:text-slate-500">
                    <FileText className="mb-2 h-7 w-7 opacity-40" />
                    <p className="text-sm">Henüz gönderi yok</p>
                  </div>
                ) : (
                  <div className="space-y-2 overflow-y-auto" style={{ maxHeight: "520px" }}>
                    {posts.map((post) => {
                      const badge = STATUS_BADGE[post.status] ?? STATUS_BADGE.draft;
                      const dateStr = formatDate(post.published_at ?? post.scheduled_at);
                      const isActing = actionId === post.id;
                      const canPublish = post.status === "draft" || post.status === "scheduled";

                      return (
                        <div key={post.id}>
                        <div
                          className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-800/30"
                        >
                          {/* Status icon */}
                          <div className="mt-0.5 shrink-0">
                            {post.status === "published" && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                            {post.status === "failed"    && <XCircle className="h-4 w-4 text-red-500" />}
                            {post.status === "scheduled" && <Clock className="h-4 w-4 text-blue-500" />}
                            {post.status === "draft"     && <FileText className="h-4 w-4 text-slate-400" />}
                          </div>

                          {/* Content */}
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm text-slate-800 dark:text-slate-200">
                              {post.caption ? post.caption.slice(0, 60) + (post.caption.length > 60 ? "…" : "") : "(boş)"}
                            </p>
                            <div className="mt-1 flex items-center gap-2">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${badge.className}`}>
                                {badge.label}
                              </span>
                              {dateStr && (
                                <span className="text-[10px] text-slate-400">{dateStr}</span>
                              )}
                            </div>
                            {post.error_message && (
                              <p className="mt-1 truncate text-[10px] text-red-500">{post.error_message}</p>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex shrink-0 items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingPost(post.id);
                                setEditCommentReply(post.comment_reply || '');
                                setEditDmContent(post.dm_content || '');
                              }}
                              className="text-xs text-blue-500 hover:text-blue-700 px-1.5 py-1"
                            >
                              Düzenle
                            </button>
                            {canPublish && (
                              <button
                                onClick={() => handlePublishPost(post.id)}
                                disabled={isActing}
                                className="flex items-center gap-1 rounded-lg bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50"
                              >
                                {isActing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                                Yayınla
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(post.id)}
                              disabled={isActing}
                              className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-50 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                            >
                              {isActing && !canPublish ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Trash2 className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Inline edit form */}
                        {editingPost === post.id && (
                          <div className="mt-2 space-y-2 rounded-lg border p-3 bg-slate-50 dark:bg-slate-800/50">
                            <div>
                              <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Yorum Yanıtı</label>
                              <textarea
                                value={editCommentReply}
                                onChange={e => setEditCommentReply(e.target.value)}
                                rows={2}
                                className="w-full mt-1 text-xs border rounded p-2 dark:bg-slate-700 dark:border-slate-600"
                                placeholder="@{username} analizin DM olarak iletildi 💌"
                              />
                              <p className="text-xs text-slate-400">{'{username}'} {'{label}'} {'{num}'}</p>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-slate-600 dark:text-slate-400">DM İçeriği</label>
                              <textarea
                                value={editDmContent}
                                onChange={e => setEditDmContent(e.target.value)}
                                rows={4}
                                className="w-full mt-1 text-xs border rounded p-2 dark:bg-slate-700 dark:border-slate-600"
                                placeholder="🔢 {label}: {num}&#10;&#10;{content}&#10;&#10;🔮 Soul Circles..."
                              />
                              <p className="text-xs text-slate-400">{'{label}'} {'{num}'} {'{content}'} {'{a}'} {'{b}'}</p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  await fetch(`${API}/posts/${post.id}`, {
                                    method: 'PATCH',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ comment_reply: editCommentReply, dm_content: editDmContent }),
                                  });
                                  setEditingPost(null);
                                  fetchPosts();
                                  toast({ title: "Kaydedildi ✅" });
                                }}
                                className="text-xs bg-purple-600 text-white px-3 py-1.5 rounded-lg"
                              >
                                Kaydet
                              </button>
                              <button
                                onClick={() => setEditingPost(null)}
                                className="text-xs border px-3 py-1.5 rounded-lg text-slate-600 dark:text-slate-300"
                              >
                                İptal
                              </button>
                            </div>
                          </div>
                        )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
